import {
  Accordion,
  Box,
  Button,
  ButtonsGroup,
  Card,
  Footer,
  Grid,
  Heading,
  InputSelect,
  InputText,
  Link,
  Maps,
  PageHeader,
  PageWrapper,
  Select,
  Typo
} from "#/atoms";
import { ChangePasswordModal, ContactModal, RemoveModal } from "@/components/organisms";
import { ContactsCard, NotesCard } from "../Order/Cards";
import { CustomerContacts, OrderTemplate } from "@/types";
import { DESKTOP_SPACING, FONT_SIZE, HPOSITION, NATURAL } from "@/theme";
import { FC, memo, useCallback, useEffect, useRef, useState } from "react";
import { addProtocolIfMissing, countryList } from "@/helpers";
import { useInput, useInputSelect, useMaps, useModal, useSelect } from "@/hooks";

import { CustomerDetailsProps } from "./CustomerDetails.interface";
import { CustomerLogoCard } from "@/components/molecules/CustomerLogoCard";
import { EditPackageModal } from "@/components/organisms/Modais/EditPackage";
import { HeadingIcon } from "#/molecules";
import { PackageList } from "../Order";
import { useNavigate } from "react-router-dom";

const CustomerDetails: FC<CustomerDetailsProps> = ({ 
  branchId,
  customerId,
  customerData,
  getBlankServices,
  isAdmin,
  loadingOrderTemplates,
  logoUrl,
  notes,
  onAddNote,
  onCancelNote,
  onSaveNote,
  onAddOrderTemplate,
  onAddNewContact,
  onEditContact,
  onChangePassword,
  onLogoUpload,
  onRemoveContact,
  onSaveAccountDetails,
  onSaveDetails,
  orderTemplates,
  owner }) => {
  
  const { mapsAddress, onBlur } = useMaps();

  const { showModal: showChangePasswordModal, onCloseModal: onCloseChangePasswordModal, onOpenModal: onOpenChangePasswordModal } = useModal(); 
  const { showModal: showEditModal, onCloseModal: onCloseEditModal, onOpenModal: onOpenEditModal } = useModal(); 
  const { showModal: showEditContactModal, onCloseModal: onCloseEditContactModal, onOpenModal: onOpenEditContactModal } = useModal(); 
  const { showModal: showRemoveContactModal, onCloseModal: onCloseRemoveContactModal, onOpenModal: onOpenRemoveContactModal } = useModal(); 

  const contactIdToDelete = useRef<string | null>(null);
  const contactmodalType = useRef<'edit' | 'create'>('create');
  const contactToEdit = useRef<CustomerContacts | null>(null);

  const navigate = useNavigate();

  const [blankOrderTemplate, setBlankOrderTemplate] = useState<OrderTemplate>();

  const login = useInput(customerData?.accountDetails.login);
  const email = useInput(customerData?.accountDetails.email);
  const accountPhone = useInput(customerData?.accountDetails.accountPhoneNumber);
  const firstName = useInput(customerData?.accountDetails.firstName);
  const lastName = useInput(customerData?.accountDetails.lastName);
  const daysToPay = useInput(customerData?.accountDetails.daysToPayInvoices?.toString());
  const website = useInput(customerData?.accountDetails.website);

  const vat = useInputSelect('vat', customerData?.address.tax);

  const code = useInput(customerData?.address.customerCode);
  const companyName = useInput(customerData?.address.name);
  const houseName = useInput(customerData?.address.houseName);
  const houseNumber = useInput(customerData?.address.houseNumber);
  const road = useInput(customerData?.address.road);
  const area = useInput(customerData?.address.area);
  const town = useInput(customerData?.address.town);
  const county = useInput(customerData?.address.county);
  const postcode = useInput(customerData?.address.postcode);
  const countryId = useSelect((customerData?.address.countryId ?? 0).toString());
  
  const active = useInputSelect('active', customerData?.address.active);
  const payByCard = useInputSelect('payByCard', customerData?.address.isCardPayment);
  const useLowQualityThumbnails = useInputSelect('useLowQualityThumbnails', customerData?.address.isLowQuality);
  const scrubData = useInputSelect('scrubData', customerData?.address.shouldRemoveData);

    useEffect(() => {
      active.setChecked(customerData?.address.active || false);
      payByCard.setChecked(customerData?.address.isCardPayment || false);
      useLowQualityThumbnails.setChecked(customerData?.address.isLowQuality || false);
      scrubData.setChecked(customerData?.address.shouldRemoveData || false);
    }, [customerData?.address])

  const handleSaveAccountDetails = async () => {
    await onSaveAccountDetails({
      AccountPhoneNumber: accountPhone.value,
      CountryId: 0,
      DaysToPayInvoices: parseInt(daysToPay.value, 10),
      DefaultPhotos: 0,
      Email: email.value,
      FirstName: firstName.value,
      Id: customerId,
      LastName: lastName.value,
      UserName: login.value,
      UserUpsellPrice: 0,
      Website: website.value,
    });
  };

  const handleChangePassword = async (oldPassword: string, newPassword: string, confirmPassword: string ) => {
    onChangePassword(oldPassword, newPassword, confirmPassword);
    onCloseChangePasswordModal();
  }

  const handleSaveDetails = async () => {
    await onSaveDetails(customerId, {
      Area: area.value,
      Branch: branchId.toString(),
      Country: county.value,
      CountryId: parseInt(countryId.value, 10),
      CustomerCode: code.value,
      HouseName: houseName.value,
      HouseNumber: houseNumber.value,
      IsActive: active.checked,
      IsCardPayment: payByCard.checked,
      IsLowQuality: useLowQualityThumbnails.checked,
      IsTaxAdded: vat.checked,
      Name: companyName.value,
      Postcode: postcode.value,
      Road: road.value,
      ShouldRemoveData: scrubData.checked,
      Town: town.value
    })
  };

  const handleEditOrderTemplate = async () => {
    const services = await getBlankServices();
    setBlankOrderTemplate({
      customerId: branchId.toString(),
      description: '',
      id: 0,
      isVirtual: false,
      price: 0,
      services: services.map(se => { return {
        checked: false,
        checkedTxt: 'no',
        defaultAmount: se.defaultAmount,
        imageUrl: se.imageUrl,
        name: se.name,
        orderTypeId: se.id
      }})
    });
    onOpenEditModal();
  }

  const handleEditContact = useCallback((contactId: string) => {
    contactmodalType.current = 'edit';
    contactToEdit.current = customerData?.contacts.find((contact) => contact.contactId === contactId) || null;
    
    if (contactToEdit.current) {
      onOpenEditContactModal();
    }
  }, [customerData?.contacts]);

  const handleEditedContact = (contact: CustomerContacts) => {
    onEditContact(contact);
    onCloseEditContactModal();
  }

  const handleNewContact = () => {
    contactmodalType.current = 'create';
    contactToEdit.current = null;
    onOpenEditContactModal();
  }

  const handleNewContactConfirmed = (contact: CustomerContacts) => {
    onAddNewContact(contact);
    onCloseEditContactModal();
  }

  const handleRemoveContact = (contactId: string) => {
    contactIdToDelete.current = contactId;
    onOpenRemoveContactModal();
  }

  const handleRemoveConfirmedContact = () => {
    if (contactIdToDelete.current) {
      onRemoveContact(contactIdToDelete.current);
      contactIdToDelete.current = null;
    }
  }

  const onSelectOrderTemplate = (id: number) => {
    navigate(`/order-new/brid=${branchId}/pkgid=${id}`);
  };

  const handleShowOrders = () => {
    navigate(`/order-find/email=${email.value}`);
  };

  return (
    <>
      <PageWrapper pb="100px">
        <PageHeader>
          <Heading scale="h1" fontColor={NATURAL.K}>Edit Customer Profile</Heading>
        </PageHeader>

        <Card padding="0">
          <Box padding={`${DESKTOP_SPACING.EL}px`}>
            <Accordion
              header={<HeadingIcon icon="contacts" title="Account Details" />}
              onToggle={() => null}
              showArrow
              state="open"
            >
              <Grid columnGap={DESKTOP_SPACING.XL} gridTemplateColumns="repeat(3, 30%)" margin={`${DESKTOP_SPACING.XXL}px 0 0`}>
                <InputText type="text" label="Company Name" value={companyName.value} onChange={companyName.onChange} />
                <InputText type="text" label="Customer Code" value={code.value} onChange={code.onChange} />
                <InputText type="text" label="Login" value={login.value} onChange={login.onChange} />
                <InputText type="text" label="First Name" value={firstName.value} onChange={firstName.onChange} />
                <InputText type="text" label="Last Name" value={lastName.value} onChange={lastName.onChange} />
                <InputText type="email" label="E-mail Address" value={email.value} onChange={email.onChange} />
                <InputText type="tel" label="Account Phone Number" value={accountPhone.value} onChange={accountPhone.onChange} />
                <InputText type="number" label="Days To Pay Invoices" value={daysToPay.value} onChange={daysToPay.onChange} />
                <Box>
                  <InputText type="url" label="Website" value={website.value} onChange={website.onChange} />
                  <Link href={addProtocolIfMissing(website.value)} target="_blank">{website.value}</Link>
                </Box>
                <InputSelect type="checkbox" checked={vat.checked} label="Customer VAT" value={vat.value} onChangeValue={vat.onChange} readOnly={!isAdmin} />
              </Grid>
              <ButtonsGroup align={HPOSITION.RIGHT} fullWidth>
                <Button onClick={() => { handleSaveAccountDetails(); handleSaveDetails(); } }>
                  Save
                </Button>
                <Button actionType="outline" onClick={() => onOpenChangePasswordModal() }>
                  Change Password
                </Button>
              </ButtonsGroup>
            </Accordion>
          </Box>
        </Card>

        <Card padding="0">
          <Grid gridTemplateColumns="repeat(2, 1fr)">
            <Box padding={`${DESKTOP_SPACING.EL}px`}>
              <HeadingIcon
                icon="location"
                title="Address Details"
              />
              <Typo fontColor={NATURAL.G} fontSize={FONT_SIZE.SM} margin="15 0">Office address:</Typo>
              <InputText type="text" label="House Name" value={houseName.value} onChange={houseName.onChange} />
              <InputText type="text" label="Number" value={houseNumber.value} onChange={houseNumber.onChange} />
              <InputText type="text" label="Road" value={road.value} onChange={road.onChange} />
              <InputText type="text" label="Area" value={area.value} onChange={area.onChange} />
              <InputText type="text" label="Town" value={town.value} onChange={town.onChange} />
              <InputText type="text" label="County" value={county.value} onChange={county.onChange} />
              <InputText type="text" label="Postcode" value={postcode.value} onBlur={onBlur} onChange={postcode.onChange} />
              <Select label="Country"
                  options={Object.keys(countryList).map((k) => { return { id: k.replace("C", ""), label: countryList[k].name} })}
                  onChange={countryId.onChange}
                  selected={countryId.value}
                />
              { isAdmin && <> <InputSelect type="checkbox" checked={active.checked} label="Is Active" value={active.value} onChangeValue={active.onChange} />
              <InputSelect type="checkbox" checked={payByCard.checked} label="Pay by Card" value={payByCard.value} onChangeValue={payByCard.onChange} />
              <InputSelect type="checkbox" checked={useLowQualityThumbnails.checked} label="Use low quality thumbnails" value={useLowQualityThumbnails.value} onChangeValue={useLowQualityThumbnails.onChange} />
              </> }
              <InputSelect type="checkbox" checked={scrubData.checked} label="Scrub personal data once order completes" value={scrubData.value} onChangeValue={scrubData.onChange} />
              <ButtonsGroup align={HPOSITION.RIGHT} fullWidth>
              { isAdmin && <Button onClick={() => handleShowOrders() }>
                  Show Orders
                </Button> }
                <Button onClick={() => handleSaveDetails() }>
                  Save
                </Button>
              </ButtonsGroup>
            </Box>
            <Box>
              <Maps address={mapsAddress} />
            </Box>
          </Grid>
        </Card>

       <ContactsCard 
          contacts={customerData?.contacts || []}
          isAdmin={isAdmin}
          isReadOnly={false}
          onAskToEditContact={handleEditContact}
          onAskToRemoveContact={handleRemoveContact}
          onNewContact={handleNewContact}
       />
      
      { isAdmin && <NotesCard
          notes={notes}
          onAddNote={onAddNote}
          onCancelNote={onCancelNote}
          onSaveNote={onSaveNote}
          owner={owner}
        /> }
      <CustomerLogoCard logoUrl={logoUrl} onFileUpload={onLogoUpload} />
     {isAdmin && <PackageList
            actionText={"New package"}
            headerText="Select Package"
            isAdmin={isAdmin || false}
            loadingOrderTemplates={loadingOrderTemplates}
            onHeaderClick={handleEditOrderTemplate}
            onSelectOrderTemplate={onSelectOrderTemplate}
            orderTemplates={orderTemplates}
            showHeader={true}
            /> }
      {showEditModal && blankOrderTemplate  && <EditPackageModal
        loading={false}
        orderTemplate={blankOrderTemplate} 
        onClose={onCloseEditModal}
        onSaveTemplate={(template) => { onAddOrderTemplate(template); onCloseEditModal(); }}
        />
      }
      {showChangePasswordModal  && <ChangePasswordModal
        onClose={onCloseChangePasswordModal}
        onChangePassword={handleChangePassword}
        />
      }
      {showRemoveContactModal && (
          <RemoveModal
              description="Are you sure you want to remove this contact?"
              onCancel={onCloseRemoveContactModal}
              onClose={onCloseRemoveContactModal}
              onRemove={handleRemoveConfirmedContact}
              title="Remove Contact"
          />
      )}
      { showEditContactModal && <ContactModal
                contact={contactToEdit.current}
                hideType={true}
                onAddNew={handleNewContactConfirmed}
                onEdit={handleEditedContact}
                onClose={onCloseEditContactModal}
                type={contactmodalType.current}
                /> }
      </PageWrapper>
      <Footer>
         <></>
      </Footer>
    </>
  );
};

export default memo(CustomerDetails);
